import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { Observable, map } from 'rxjs';
import {
  AssignExistingManagerRequest,
  CreateUserRequest,
  UpdateUserRequest,
  HotelUser,
  ApiResponse
} from '../interfaces/hotel-manager';
import { PaginatedResponse } from '../interfaces/api-response';
import { TwoFactorResetResponse } from '../interfaces/two-factor';
import { PermissionService } from './permission.service';

/** Access area item returned from GET /v2/extranet/access-areas */
export interface AccessAreaItem {
  id: number;
  slug: string;
  name: string;
  description: string;
  sort_order: number;
}

/** Response shape from GET /v2/extranet/hotels/:hotelId/users/:userId/access-areas */
export interface UserAccessAreasResponse {
  user_id: number;
  hotel_id: number;
  access_areas: string[];
}

interface UserFilters {
  search?: string;
  is_active?: boolean;
  hotel_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExtranetUserService {

  constructor(
    private http: HttpService,
    private permissionService: PermissionService,
  ) {}

  getUsers(filters?: UserFilters): Observable<ApiResponse<HotelUser[]>> {
    const params: Record<string, string | boolean | number> = {};
    if (filters) {
      if (filters.search) params['search'] = filters.search;
      if (filters.is_active !== undefined) params['is_active'] = filters.is_active;
      if (filters.hotel_id) params['hotel_id'] = filters.hotel_id;
    }

    return this.http.get<ApiResponse<HotelUser[]>>('v2/extranet/users', { params });
  }

  /**
   * Get every hotel manager (internal_role IS NULL) across all hotels.
   * Powers the Super-Admin "All Hotel Manager" page.
   */
  getAllManagers(filters?: {
    search?: string;
    is_active?: boolean;
    page?: number;
    perPage?: number;
  }): Observable<PaginatedResponse<HotelUser>> {
    const params: Record<string, string | boolean | number> = {};
    if (filters?.search) params['search'] = filters.search;
    if (filters?.is_active !== undefined) params['is_active'] = filters.is_active;
    if (filters?.page) params['page'] = filters.page;
    if (filters?.perPage) params['per_page'] = filters.perPage;

    return this.http.get<PaginatedResponse<HotelUser>>('v2/extranet/managers', { params });
  }

  getUsersByHotel(hotelId: number): Observable<ApiResponse<HotelUser[]>> {
    // Internal users use the admin endpoint; hotel managers use the hotel-scoped endpoint
    if (this.permissionService.isInternal()) {
      return this.getUsers({ hotel_id: hotelId });
    }

    // Hotel-scoped endpoint returns a flat array of extranet users
    return this.http.get<{ data: HotelUser[] }>(`v2/hotels/managers/${hotelId}`).pipe(
      map(response => ({
        success: true,
        data: response.data,
      }))
    );
  }

  getUser(id: number, hotelId: number): Observable<ApiResponse<HotelUser>> {
    if (this.permissionService.isInternal()) {
      return this.http.get<ApiResponse<HotelUser>>(`v2/extranet/users/${id}`);
    }

    return this.http.get<{ data: HotelUser }>(`v2/hotels/managers/${hotelId}/${id}`).pipe(
      map(response => ({
        success: true,
        data: response.data,
      }))
    );
  }

  createUser(data: CreateUserRequest, hotelId?: number | null): Observable<ApiResponse<HotelUser>> {
    if (this.permissionService.isInternal()) {
      return this.http.post<ApiResponse<HotelUser>>('v2/extranet/users', data);
    }

    return this.http.post<ApiResponse<HotelUser>>(`v2/hotels/managers/${hotelId}`, data).pipe(
      map(response => ({
        success: true,
        data: response.data,
      }))
    );
  }

  updateUser(id: number, data: UpdateUserRequest, hotelId?: number): Observable<ApiResponse<HotelUser>> {
    if (this.permissionService.isInternal()) {
      return this.http.put<ApiResponse<HotelUser>>(`v2/extranet/users/${id}`, data);
    }

    return this.http.put<ApiResponse<HotelUser>>(`v2/hotels/managers/${hotelId}/${id}`, data).pipe(
      map(response => ({
        success: response.status === 'success',
        data: response.data
      }))
    );
  }

  deleteUser(id: number, hotelId?: number | null): Observable<ApiResponse<null>> {
    if (this.permissionService.isInternal()) {
      return this.http.delete<ApiResponse<null>>(`v2/extranet/users/${id}`);
    }

    return this.http.delete<ApiResponse<null>>(`v2/hotels/managers/${hotelId}/${id}`);
  }

  assignHotels(userId: number, hotelIds: number[]): Observable<ApiResponse<HotelUser>> {
    if (this.permissionService.isInternal()) {
      return this.http.post<ApiResponse<HotelUser>>(`v2/extranet/users/${userId}/hotels`, { hotel_ids: hotelIds });
    }
    return this.http.post<ApiResponse<HotelUser>>(`v2/hotels/managers/${hotelIds[0]}/${userId}/hotels`, { hotel_ids: hotelIds });
  }

  getAssignableManagers(hotelId: number, search?: string): Observable<ApiResponse<HotelUser[]>> {
    const params: Record<string, string> = {};
    if (search) {
      params['search'] = search;
    }

    return this.http.get<ApiResponse<HotelUser[]>>(`v2/hotels/managers/${hotelId}/available`, { params }).pipe(
      map(response => ({
        success: response.success === true || response.status === 'success',
        status: response.status,
        data: response.data,
        message: response.message
      }))
    );
  }

  assignExistingManager(
    hotelId: number,
    userId: number,
    data: AssignExistingManagerRequest
  ): Observable<ApiResponse<HotelUser>> {
    return this.http.post<ApiResponse<HotelUser>>(
      `v2/hotels/managers/${hotelId}/${userId}/assign-existing`,
      data
    ).pipe(
      map(response => ({
        success: response.success === true || response.status === 'success',
        status: response.status,
        data: response.data,
        message: response.message
      }))
    );
  }

  unassignManager(hotelId: number, userId: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`v2/hotels/managers/${hotelId}/${userId}/unassign`).pipe(
      map(response => ({
        success: response.success === true || response.status === 'success',
        status: response.status,
        data: response.data,
        message: response.message
      }))
    );
  }

  /**
   * Login as a specific user (impersonation).
   * Returns a new auth token for the target user.
   * Only Super Admins can use this feature.
   */
  loginAsUser(userId: number): Observable<ApiResponse<{
    token: string;
    user: HotelUser;
    impersonated_by: { id: number; name: string };
  }>> {
    return this.http.post<ApiResponse<{
      token: string;
      user: HotelUser;
      impersonated_by: { id: number; name: string };
    }>>(`v2/extranet/users/${userId}/login-as`, {});
  }

  /**
   * Exit impersonation and return to original admin session.
   * Generates a new token for the original admin user.
   */
  exitImpersonation(adminUserId: number, targetUserId: number): Observable<ApiResponse<{
    token: string;
    user: HotelUser;
  }>> {
    return this.http.post<ApiResponse<{
      token: string;
      user: HotelUser;
    }>>('v2/extranet/users/exit-impersonation', {
      admin_user_id: adminUserId,
      target_user_id: targetUserId
    });
  }

  /**
   * Support reset of another user's 2FA (HD-2013). Super-admin only; the
   * backend removes every authenticator, the recovery codes and all trusted
   * devices, and emails the user. The id is an extranet_users id.
   */
  resetTwoFactor(userId: number): Observable<TwoFactorResetResponse> {
    return this.http.post<TwoFactorResetResponse>(`v2/extranet/users/${userId}/2fa/reset`, {});
  }

  getAccessAreas(): Observable<ApiResponse<AccessAreaItem[]>> {
    return this.http.get<ApiResponse<AccessAreaItem[]>>('v2/extranet/access-areas');
  }

  getUserAccessAreas(hotelId: number, userId: number): Observable<ApiResponse<UserAccessAreasResponse>> {
    if (this.permissionService.isInternal()) {
      return this.http.get<ApiResponse<UserAccessAreasResponse>>(
        `v2/extranet/hotels/${hotelId}/users/${userId}/access-areas`
      );
    }

    return this.http.get<ApiResponse<UserAccessAreasResponse>>(
      `v2/hotels/managers/${hotelId}/${userId}/access-areas`
    ).pipe(
      map(response => ({
        success: true,
        data: response.data,
      }))
    );
  }

  /**
   * Replace all access areas for a user at a specific hotel.
   * Enforces privilege escalation on the backend.
   */
  assignAccessAreas(hotelId: number, userId: number, slugs: string[]): Observable<ApiResponse<void>> {
    if (this.permissionService.isInternal()) {
      return this.http.put<ApiResponse<void>>(
        `v2/extranet/hotels/${hotelId}/users/${userId}/access-areas`,
        { access_areas: slugs }
      );
    }

    return this.http.put<ApiResponse<void>>(
      `v2/hotels/managers/${hotelId}/${userId}/access-areas`,
      { access_areas: slugs }
    );
  }
}
